import { formatKes } from "lib/format";
import { useEffect, useMemo, useState } from "react";
import { MdTrendingUp, MdAccountBalanceWallet, MdHourglassTop, MdPaid } from "react-icons/md";
import { createColumnHelper } from "@tanstack/react-table";
import Card from "components/card";
import Widget from "components/widget/Widget";
import StatusBadge from "components/admin/StatusBadge";
import ExpandableDataTable from "components/admin/ExpandableDataTable";
import { DetailSection } from "components/admin/DetailPanel";
import { apiGet } from "lib/apiClient";

const statusColumnHelper = createColumnHelper();
const SETTLEMENT_ROLLUP_PAGE_SIZE = 100;
const STATUS_TONE = {
  CALCULATED: "gray",
  PENDING_APPROVAL: "amber",
  APPROVED: "blue",
  PROCESSING: "blue",
  PAID: "green",
  FAILED: "red",
  REVERSED: "red",
};

// No money arithmetic happens in this file any more. Every total below comes from
// /portal/reports, which sums in SQL over every settlement period that has ever
// existed. This page used to add them up here with Number(), which was wrong twice:
// truncated to one page, and floating point on money.

/**
 * A rolled-up financial report for the organization: what's been earned and paid out
 * to date, broken down by settlement status, plus how much has already accrued since
 * the last settlement calculation ran. Everything here is derived from the same data
 * the Revenue and Settlements pages show individually — this page's job is the totals
 * those don't compute for you.
 */
export default function ReportsPage() {
  const [report, setReport] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // The report carries every total, summed server-side over all periods; the revenue
    // page is fetched only to list the individual periods behind an expanded status row.
    Promise.all([apiGet("/portal/reports"), apiGet(`/portal/revenue?page=1&pageSize=${SETTLEMENT_ROLLUP_PAGE_SIZE}`)])
      .then(([reportResult, revenueResult]) => {
        if (cancelled) return;
        setReport(reportResult);
        setRevenue(revenueResult);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load report data.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const settlements = useMemo(() => revenue?.settlements || [], [revenue]);
  const currency = report?.currency || report?.accrued?.currency || "";

  const totalPaid = report?.paidClientShare ?? "0";
  const totalPending = report?.pendingClientShare ?? "0";
  const lifetimeGross = report?.lifetimeGrossRevenue ?? "0";

  const statusRows = (report?.byStatus || []).map((group) => ({
    ...group,
    clientShare: group.clientShareAmount,
    // Drill-down detail only. The totals on this row already cover every period at this
    // status, including any beyond the page fetched above.
    rows: settlements.filter((s) => s.status === group.status),
  }));

  const statusColumns = [
    statusColumnHelper.accessor("status", {
      header: () => <p>STATUS</p>,
      cell: (info) => <StatusBadge label={info.getValue()} tone={STATUS_TONE[info.getValue()] || "gray"} />,
    }),
    statusColumnHelper.accessor("periodCount", {
      header: () => <p>PERIODS</p>,
      cell: (info) => <p className="num text-sm text-navy-700 dark:text-white">{info.getValue()}</p>,
    }),
    statusColumnHelper.accessor("clientShare", {
      header: () => <p>YOUR SHARE</p>,
      cell: (info) => (
        <p className="num text-sm font-semibold text-navy-700 dark:text-white">{formatKes(info.getValue(), currency)}</p>
      ),
    }),
  ];

  function renderStatusPanel(group) {
    return (
      <div>
        <DetailSection
          title="How this total was built"
          description="Every settlement period currently at this status, added together."
        >
          <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-white/10">
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 text-sm dark:border-white/5">
              <span className="text-gray-600 dark:text-gray-300">Gross revenue across {group.periodCount} period(s)</span>
              <span className="num text-navy-700 dark:text-white">{formatKes(group.grossRevenue, currency)}</span>
            </div>
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 text-sm dark:border-white/5">
              <span className="text-gray-600 dark:text-gray-300">Deductions</span>
              <span className="num text-navy-700 dark:text-white">{formatKes(group.deductions, currency)}</span>
            </div>
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 text-sm dark:border-white/5">
              <span className="text-gray-600 dark:text-gray-300">Net revenue</span>
              <span className="num font-semibold text-navy-700 dark:text-white">{formatKes(group.netRevenue, currency)}</span>
            </div>
            <div className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="text-gray-600 dark:text-gray-300">Your share</span>
              <span className="num font-semibold text-navy-700 dark:text-white">{formatKes(group.clientShare, currency)}</span>
            </div>
          </div>
        </DetailSection>

        <DetailSection title="Periods at this status">
          <div className="max-h-56 overflow-y-auto rounded-xl border border-gray-200 dark:border-white/10">
            {group.rows.map((s) => (
              <div
                key={s.id}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2 text-xs last:border-0 dark:border-white/5"
              >
                <span className="text-gray-600 dark:text-gray-300">
                  {new Date(s.periodStart).toLocaleDateString()} to {new Date(s.periodEnd).toLocaleDateString()}
                </span>
                <span className="num font-semibold text-navy-700 dark:text-white">
                  {formatKes(s.clientShareAmount, s.currency || currency)}
                </span>
              </div>
            ))}
          </div>
        </DetailSection>
      </div>
    );
  }

  if (loading) return <p className="text-sm text-gray-500">Loading report…</p>;
  if (error) return <div className="rounded-xl bg-red-50 p-4 text-sm font-medium text-red-500">{error}</div>;

  return (
    <div>
      <div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        <Widget
          icon={<MdTrendingUp className="h-7 w-7" />}
          title="Accrued (not yet settled)"
          subtitle={formatKes(revenue?.accrued?.accruedClientShare || 0, revenue?.accrued?.currency || "KES")}
        />
        <Widget icon={<MdHourglassTop className="h-7 w-7" />} title="Pending payout" subtitle={formatKes(totalPending, currency)} />
        <Widget icon={<MdPaid className="h-7 w-7" />} title="Paid out to date" subtitle={formatKes(totalPaid, currency)} />
        <Widget icon={<MdAccountBalanceWallet className="h-7 w-7" />} title="Lifetime gross revenue" subtitle={formatKes(lifetimeGross, currency)} />
      </div>

      <Card extra="p-5 mb-5">
        <p className="text-lg font-semibold text-navy-700 dark:text-white">Currently accruing</p>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Revenue generated since the last settlement was calculated — it moved on the ledger the moment each market settled, but hasn't
          been grouped into a settlement period and paid out yet.
        </p>
        {revenue?.accrued?.eventCount > 0 ? (
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
              <p className="text-xs font-medium text-muted-foreground">Gross revenue</p>
              <p className="mt-1 text-lg font-semibold text-navy-700 dark:text-white">{formatKes(revenue.accrued.grossRevenue, revenue.accrued.currency)}</p>
            </div>
            <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
              <p className="text-xs font-medium text-muted-foreground">Your projected share</p>
              <p className="mt-1 text-lg font-semibold text-navy-700 dark:text-white">{formatKes(revenue.accrued.accruedClientShare, revenue.accrued.currency)}</p>
            </div>
            <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
              <p className="text-xs font-medium text-muted-foreground">Contributing events</p>
              <p className="mt-1 text-lg font-semibold text-navy-700 dark:text-white">{revenue.accrued.eventCount}</p>
            </div>
          </div>
        ) : (
          <p className="mt-3 text-sm text-gray-500">Nothing has accrued since the last settlement was calculated.</p>
        )}
      </Card>

      <Card extra="p-5">
        <p className="text-lg font-semibold text-navy-700 dark:text-white">Settlement history by status</p>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          {`Totals cover every settlement period ever calculated${
            revenue?.total > SETTLEMENT_ROLLUP_PAGE_SIZE
              ? `. Expanding a row lists the ${SETTLEMENT_ROLLUP_PAGE_SIZE} most recent of them, out of ${revenue.total}.`
              : "."
          }`}
        </p>
        {statusRows.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">No settlements have been calculated yet.</p>
        ) : (
          <div className="mt-3">
            <ExpandableDataTable
              columns={statusColumns}
              data={statusRows}
              emptyMessage="No settlements have been calculated yet."
              exportFilename="settlements-by-status"
              exportColumns={[
                { accessorKey: "status", header: "status" },
                { accessorKey: "periodCount", header: "periods" },
                { accessorKey: "grossRevenue", header: "grossRevenue" },
                { accessorKey: "netRevenue", header: "netRevenue" },
                { accessorKey: "clientShare", header: "yourShare" },
              ]}
              getRowId={(row) => row.status}
              renderDetail={(g) => renderStatusPanel(g)}
            />
          </div>
        )}
      </Card>
    </div>
  );
}
